import React, { useState, useEffect } from 'react';
import {
  X,
  Package,
  Filter,
  List,
  ArrowRight,
  MapPin,
  AlertTriangle
} from 'lucide-react';
import { POrderLineDto } from '../../../../../types/porders';
import { stcTransService, StcTransDto } from '../../../../../services/stcTransService';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { useDirectPlacementEnabled } from '../../../../../hooks/useDirectPlacementEnabled';
import { useReceiptExtraInfoSlots } from '../hooks/useReceiptExtraInfoFields';
import { resolveReceiptExtraInfo, resolveReceiptExtraInfoValues } from '../helpers';
import { ReceiptExtraInfo } from '../ReceiptExtraInfo';

/**
 * Ordrelinje beriket med beregnet ankommet/mottatt mengde. Eksporteres slik at
 * "Legg direkte ut på lokasjon"-flyten (P19/P20) kan forhåndsutfylle gjenstående antall.
 */
export interface LineWithArrived extends POrderLineDto {
  arrivedQuantity: number; // Ankommet (sum av StcTrans.Quantity der LineStatus = 20)
  receivedQuantity: number; // Mottatt (sum av StcTrans.Quantity der LineStatus > 20)
}

interface POrderLineBrowserProps {
  orderNo: number;
  orderLines: POrderLineDto[];
  onLineSelect: (line: POrderLineDto) => void;
  onAllLinesSelect?: (lines: POrderLineDto[]) => void; // Ny callback for bulk-operasjon
  /** Norlux P19/P20: legg valgte linjer direkte ut på lokasjon (uten ankomstregistrering) */
  onDirectPlacement?: (lines: LineWithArrived[]) => void;
  onClose: () => void;
  batchId?: string; // Valgfri: For bilmottak/forsendelse der batchId allerede er kjent
}

export const POrderLineBrowser: React.FC<POrderLineBrowserProps> = ({
  orderNo,
  orderLines,
  onLineSelect,
  onAllLinesSelect,
  onDirectPlacement,
  onClose,
  batchId: externalBatchId
}) => {
  const { t } = useTranslation();
  // P19/P20: gates hele direkte-utlegg-funksjonen bak systemparameter (av som standard).
  const directPlacementEnabled = useDirectPlacementEnabled();
  const extraInfoSlots = useReceiptExtraInfoSlots();
  const [searchValue, setSearchValue] = useState('');
  const [filteredLines, setFilteredLines] = useState<LineWithArrived[]>([]);
  const [linesWithArrived, setLinesWithArrived] = useState<LineWithArrived[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());

  // Avhukning av enkeltlinjer + "Motta valgte" (punkt 16)
  const lineKey = (line: POrderLineDto) => `${line.orderNo}-${line.lineNo}`;
  // Ukjent vare (Lag 1): finnes ikke i Items-master → kan ikke mottas (StcTrans-vakten avviser ved
  // lagring). Markeres rødt og ekskluderes fra valg/bulk så operatøren ikke prøver forgjeves.
  const isUnknownItem = (line: POrderLineDto) => line.itemExistsInMaster === false;
  const selectableFilteredLines = filteredLines.filter((l) => !isUnknownItem(l));
  const unknownItemCount = filteredLines.filter(isUnknownItem).length;
  const allFilteredSelected =
    selectableFilteredLines.length > 0 && selectableFilteredLines.every((l) => selectedKeys.has(lineKey(l)));
  const selectedLines = filteredLines.filter((l) => selectedKeys.has(lineKey(l)));

  // Funksjon for å velge alle filtrerte linjer (kun varer som finnes i master)
  const handleSelectAllLines = () => {
    if (onAllLinesSelect) {
      // Hvis bulk-callback er definert, bruk den
      onAllLinesSelect(selectableFilteredLines);
    } else {
      // Fallback til å kalle onLineSelect for hver linje
      selectableFilteredLines.forEach(line => {
        onLineSelect(line);
      });
    }
  };

  const toggleLine = (line: POrderLineDto) => {
    if (isUnknownItem(line)) return; // Ukjent vare kan ikke velges
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      const key = lineKey(line);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        selectableFilteredLines.forEach((l) => next.delete(lineKey(l)));
      } else {
        selectableFilteredLines.forEach((l) => next.add(lineKey(l)));
      }
      return next;
    });
  };

  const handleReceiveSelected = () => {
    if (selectedLines.length === 0) return;
    if (onAllLinesSelect) {
      onAllLinesSelect(selectedLines);
    } else {
      selectedLines.forEach((line) => onLineSelect(line));
    }
  };

  // Norlux P19/P20: legg valgte linjer direkte ut på lokasjon (uten ankomstregistrering)
  const handleDirectPlacement = () => {
    if (selectedLines.length === 0 || !onDirectPlacement) return;
    onDirectPlacement(selectedLines);
  };

  // Hent StcTrans og beregn "Ankommet" for hver linje
  useEffect(() => {
    const loadArrivedQuantities = async () => {
      try {
        let allTransactions: StcTransDto[];

        if (externalBatchId) {
          // Bilmottak/forsendelse: batchId er allerede kjent, hent transaksjoner direkte
          allTransactions = await stcTransService.getTransactionsByBatch(externalBatchId);
        } else {
          // Enkeltordre (Modus 1): Hent BatchID basert på orderNo
          const batchId = await stcTransService.getOrCreateBatchId(orderNo);
          allTransactions = await stcTransService.getTransactionsByBatch(batchId);
        }

        // Beregn ankommet og mottatt antall per linje
        const linesWithArrivedData: LineWithArrived[] = orderLines.map(line => {
          // Filtrer StcTrans for denne linjen - bruk linjens egen orderNo (viktig for multi-ordre)
          const lineOrderNo = line.orderNo ?? orderNo;
          const lineTransactions = allTransactions.filter(
            (t: StcTransDto) => t.orderNo === lineOrderNo && t.lineNo === line.lineNo
          );
          // Ankommet: sum av StcTrans.Quantity der LineStatus = 20 (UnderArbeid) i aktiv batch (in-progress,
          // ikke ennå ferdigstilt til QuantityDelivered).
          const arrivedQty = lineTransactions
            .filter((t: StcTransDto) => t.lineStatus === 20)
            .reduce(
              (sum: number, t: StcTransDto) => sum + (t.quantity || 0),
              0
            );
          // Mottatt: KUMULATIV levert mengde fra ordrelinjen (QuantityDelivered) — autoritativ på tvers av
          // ALLE batcher og direkte utlegg. Tidligere summerte vi StcTrans>20 fra ÉN batch, som ga 0 for
          // linjer mottatt via direkte utlegg (egne batcher pr. operasjon) → ferdig mottatte linjer dukket
          // feilaktig opp som mottakbare igjen (fare for dobbel-mottak).
          const receivedQty = line.quantityDelivered || 0;

          return {
            ...line,
            arrivedQuantity: arrivedQty,
            receivedQuantity: receivedQty
          };
        });

        // Filtrer bort ferdigmottatte linjer (kun vis linjer det er aktuelt å jobbe med)
        const activeLines = linesWithArrivedData.filter(line => {
          const bestilt = line.quantity || 0;
          if (bestilt === 0) return false; // Ingen bestilt mengde – ikke relevant
          const totalBehandlet = (line.receivedQuantity || 0) + (line.arrivedQuantity || 0);
          return totalBehandlet < bestilt; // Vis kun linjer som ikke er fullt behandlet
        });

        setLinesWithArrived(activeLines);
      } catch (error) {
        console.error('Kunne ikke hente StcTrans:', error);
        // Fallback: sett arrivedQuantity og receivedQuantity til 0
        const linesWithZero: LineWithArrived[] = orderLines.map(line => ({
          ...line,
          arrivedQuantity: 0,
          receivedQuantity: 0
        }));
        setLinesWithArrived(linesWithZero);
      }
    };

    loadArrivedQuantities();
  }, [orderNo, orderLines, externalBatchId]);

  // Filtrer linjer basert på søketerm
  useEffect(() => {
    if (!searchValue) {
      setFilteredLines(linesWithArrived);
      return;
    }

    const filtered = linesWithArrived.filter(line =>
      line.itemNo.toLowerCase().includes(searchValue.toLowerCase()) ||
      (line.description1 || '').toLowerCase().includes(searchValue.toLowerCase()) ||
      (line.itemBarCode || '').toLowerCase().includes(searchValue.toLowerCase())
    );
    setFilteredLines(filtered);
  }, [searchValue, linesWithArrived]);

  const getStatusColor = (remainingQuantity?: number, totalQuantity?: number) => {
    if (!remainingQuantity || !totalQuantity) return 'bg-gray-100 text-gray-800';
    
    if (remainingQuantity === 0) return 'bg-green-100 text-green-800';
    if (remainingQuantity < totalQuantity) return 'bg-yellow-100 text-yellow-800';
    return 'bg-blue-100 text-blue-800';
  };

  const getStatusText = (remainingQuantity?: number, totalQuantity?: number) => {
    if (!remainingQuantity || !totalQuantity) return t('common.status.unknown', {}, 'Ukjent');
    
    if (remainingQuantity === 0) return t('warehouse.modules.receipt.receiptStatus.completelyReceived', {}, 'Komplett mottatt');
    if (remainingQuantity < totalQuantity) return t('warehouse.modules.receipt.receiptStatus.partiallyReceived', {}, 'Delvis mottatt');
    return t('warehouse.modules.receipt.receiptStatus.notReceived', {}, 'Ikke mottatt');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
        <div className="p-4 sm:p-6">
          {/* Header - Responsive */}
          <div className="flex items-start sm:items-center justify-between mb-4 sm:mb-6">
            <div className="flex items-start sm:items-center space-x-2 sm:space-x-3 flex-1">
              <List className="h-5 w-5 sm:h-6 sm:w-6 text-green-500 mt-0.5 sm:mt-0 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 break-words">
                  <span className="hidden sm:inline">{t('warehouse.modules.receipt.lineBrowser.allOrderLines', {}, 'Alle ordrelinjer')} - </span>
                  {externalBatchId
                    ? t('warehouse.modules.receipt.orderHeader', { orderNo: externalBatchId }, `Ordre ${externalBatchId}`)
                    : t('warehouse.modules.receipt.orderHeader', { orderNo }, `Ordre ${orderNo}`)}
                </h2>
                <p className="text-gray-600 text-xs sm:text-sm mt-1">
                  {t('warehouse.modules.receipt.lineBrowser.selectOrderLine', {}, 'Velg en ordrelinje fra listen nedenfor')}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 ml-2 flex-shrink-0"
            >
              <X className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>
          </div>

          {/* Filter - Responsive */}
          <div className="mb-4 sm:mb-6">
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder={t('warehouse.modules.receipt.lineBrowser.filterPlaceholder', {}, 'Varenr., beskrivelse eller strekkode')}
                className="w-full pl-10 pr-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
              />
            </div>
          </div>

          {/* Statistics - Responsive */}
          <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4 sm:mb-6">
            <div className="bg-green-50 rounded-lg p-3 sm:p-4">
              <div className="flex flex-col sm:flex-row sm:items-center">
                <Package className="h-6 w-6 sm:h-8 sm:w-8 text-green-500 mb-1 sm:mb-0" />
                <div className="sm:ml-3">
                  {/* Under sm ble tidligere KVALIFIKATOREN skjult og det generiske ordet
                      "mottatt" beholdt — da viste alle tre kortene samme tekst, og bare fargen
                      skilte dem. Naa vises en kort, men betydningsbaerende variant i stedet
                      (TW 36335759). */}
                  <p className="text-xs sm:text-sm font-medium text-green-600">
                    <span className="sm:hidden">{t('warehouse.modules.receipt.lineBrowser.statusShortReceived', {}, 'Mottatt')}</span>
                    <span className="hidden sm:inline">{t('warehouse.modules.receipt.lineBrowser.linesReceived', {}, 'Antall linjer')} {t('warehouse.modules.receipt.fields.received', {}, 'mottatt')}</span>
                  </p>
                  <p className="text-lg sm:text-2xl font-bold text-green-900">
                    {filteredLines.filter(line => {
                      const totalReceived = (line.receivedQuantity || 0) + (line.arrivedQuantity || 0);
                      return totalReceived >= (line.quantity || 0) && (line.quantity || 0) > 0;
                    }).length}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-yellow-50 rounded-lg p-3 sm:p-4">
              <div className="flex flex-col sm:flex-row sm:items-center">
                <Package className="h-6 w-6 sm:h-8 sm:w-8 text-yellow-500 mb-1 sm:mb-0" />
                <div className="sm:ml-3">
                  <p className="text-xs sm:text-sm font-medium text-yellow-600">
                    <span className="sm:hidden">{t('warehouse.modules.receipt.lineBrowser.statusShortPartial', {}, 'Delvis')}</span>
                    <span className="hidden sm:inline">{t('warehouse.modules.receipt.lineBrowser.partiallyPrefix', {}, 'Delvis')} {t('warehouse.modules.receipt.fields.received', {}, 'mottatt')}</span>
                  </p>
                  <p className="text-lg sm:text-2xl font-bold text-yellow-900">
                    {filteredLines.filter(line => {
                      const totalReceived = (line.receivedQuantity || 0) + (line.arrivedQuantity || 0);
                      const ordered = line.quantity || 0;
                      return totalReceived > 0 && totalReceived < ordered;
                    }).length}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
              <div className="flex flex-col sm:flex-row sm:items-center">
                <Package className="h-6 w-6 sm:h-8 sm:w-8 text-gray-500 mb-1 sm:mb-0" />
                <div className="sm:ml-3">
                  <p className="text-xs sm:text-sm font-medium text-gray-600">
                    <span className="sm:hidden">{t('warehouse.modules.receipt.lineBrowser.statusShortNotReceived', {}, 'Ikke mottatt')}</span>
                    <span className="hidden sm:inline">{t('warehouse.modules.receipt.lineBrowser.notPrefix', {}, 'Ikke')} {t('warehouse.modules.receipt.fields.received', {}, 'mottatt')}</span>
                  </p>
                  <p className="text-lg sm:text-2xl font-bold text-gray-900">
                    {filteredLines.filter(line => {
                      const totalReceived = (line.receivedQuantity || 0) + (line.arrivedQuantity || 0);
                      return totalReceived === 0;
                    }).length}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Desktop Table View - Hidden on mobile/tablet */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden hidden lg:block">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h3 className="text-lg font-semibold text-gray-900">
                  {t('warehouse.modules.receipt.orderLines', {}, 'Ordrelinjer')} ({filteredLines.length})
                </h3>
                {unknownItemCount > 0 && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-700">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    {t('warehouse.modules.receipt.lineBrowser.unknownItemCount', { 0: unknownItemCount }, `${unknownItemCount} mangler vare`)}
                  </span>
                )}
              </div>
              {filteredLines.length > 0 && (
                <div className="flex items-center gap-2">
                  {onDirectPlacement && directPlacementEnabled && (
                    <button
                      onClick={handleDirectPlacement}
                      disabled={selectedLines.length === 0}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-amber-600 hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <MapPin className="h-4 w-4 mr-2" />
                      {t('warehouse.modules.receipt.lineBrowser.directPlacement', {}, 'Legg direkte ut på lokasjon')} ({selectedLines.length})
                    </button>
                  )}
                  <button
                    onClick={handleReceiveSelected}
                    disabled={selectedLines.length === 0}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <ArrowRight className="h-4 w-4 mr-2" />
                    {t('warehouse.modules.receipt.lineBrowser.receiveSelected', {}, 'Motta valgte')} ({selectedLines.length})
                  </button>
                  <button
                    onClick={handleSelectAllLines}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
                  >
                    <Package className="h-4 w-4 mr-2" />
                    {t('warehouse.modules.receipt.lineBrowser.allLines', {}, 'Alle linjer')}
                  </button>
                </div>
              )}
            </div>
            
            {filteredLines.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Package className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                <p className="text-lg font-medium">{t('warehouse.modules.receipt.lineBrowser.noLinesFound', {}, 'Ingen ordrelinjer funnet')}</p>
                <p className="text-sm">{t('warehouse.modules.receipt.lineBrowser.tryAdjustFilter', {}, 'Prøv å justere filteret eller kontroller ordrenummeret')}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={allFilteredSelected}
                          onChange={toggleSelectAll}
                          aria-label={t('warehouse.modules.receipt.lineBrowser.selectAll', {}, 'Velg alle')}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('warehouse.general.itemNumber', {}, 'Varenummer')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('common.fields.description', {}, 'Beskrivelse')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('warehouse.modules.receipt.fields.ordered', {}, 'Bestilt')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('warehouse.modules.receipt.fields.received', {}, 'Mottatt')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('warehouse.modules.receipt.fields.arrived', {}, 'Ankommet')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                  {t('common.buttons.action', {}, 'Handling')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredLines.map((line) => {
                      const bestilt = line.quantity || 0;
                      const mottatt = line.receivedQuantity || 0;
                      const ankommet = line.arrivedQuantity || 0;

                      // Bestem bakgrunnsfarge basert på totalt behandlet (mottatt + ankommet)
                      const totalBehandlet = mottatt + ankommet;
                      const unknown = isUnknownItem(line);
                      let rowBgColor = '';
                      if (unknown) {
                        rowBgColor = 'bg-red-50'; // Ukjent vare — kan ikke mottas
                      } else if (totalBehandlet >= bestilt && bestilt > 0) {
                        rowBgColor = 'bg-green-50'; // Fullstendig mottatt
                      } else if (totalBehandlet > 0 && totalBehandlet < bestilt) {
                        rowBgColor = 'bg-yellow-50'; // Delvis mottatt
                      }

                      return (
                        <tr key={`${line.orderNo}-${line.lineNo}`} className={`hover:bg-gray-100 ${rowBgColor}`}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <input
                              type="checkbox"
                              checked={selectedKeys.has(lineKey(line))}
                              onChange={() => toggleLine(line)}
                              disabled={unknown}
                              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-40 disabled:cursor-not-allowed"
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {line.itemNo}
                            </div>
                            {unknown && (
                              <div className="mt-1 inline-flex items-center gap-1 rounded bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-700">
                                <AlertTriangle className="h-3 w-3" />
                                {t('warehouse.modules.receipt.lineBrowser.itemNotInMaster', {}, 'Finnes ikke i varemaster')}
                              </div>
                            )}
                            <div className="text-xs text-gray-500">
                              {externalBatchId && line.orderNo
                                ? `${t('warehouse.modules.receipt.orderHeader', { orderNo: line.orderNo }, `Ordre ${line.orderNo}`)}, ${t('warehouse.modules.receipt.lineNumber', { lineNo: line.lineNo }, `Rad ${line.lineNo}`)}`
                                : t('warehouse.modules.receipt.lineNumber', { lineNo: line.lineNo }, `Rad ${line.lineNo}`)}
                              {resolveReceiptExtraInfo(line, extraInfoSlots[0]) && (
                                <span className="ml-1.5 text-gray-700 font-medium">
                                  · {resolveReceiptExtraInfo(line, extraInfoSlots[0])}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">
                              {line.description1 || t('common.noDescription', {}, 'Ingen beskrivelse')}
                            </div>
                            <ReceiptExtraInfo
                              compact
                              className="mt-1"
                              values={resolveReceiptExtraInfoValues(line, [extraInfoSlots[1], extraInfoSlots[2]])}
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {bestilt} {line.itemUnitTxt}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {mottatt} {line.itemUnitTxt}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {ankommet} {line.itemUnitTxt}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              onClick={() => onLineSelect(line)}
                              disabled={unknown}
                              title={unknown ? t('warehouse.modules.receipt.lineBrowser.itemNotInMasterHint', { 0: line.itemNo }, `Vare ${line.itemNo} finnes ikke i varemaster — importer/opprett varen først`) : undefined}
                              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-blue-600"
                            >
                              <ArrowRight className="h-4 w-4 mr-1" />
                              {t('common.buttons.select', {}, 'Velg')}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Mobile/Tablet Card View - Shown on small screens */}
          <div className="lg:hidden">
            {/* Knappene la seg oppaa overskriften paa smal skjerm. Under sm brytes de
                til egen rad under tittelen; fra sm og opp er raden som foer
                (TW 36335759). */}
            <div className="mb-3 flex flex-col items-stretch gap-2 px-1 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-2 min-w-0">
                <h3 className="text-base font-semibold text-gray-900">
                  {t('warehouse.modules.receipt.orderLines', {}, 'Ordrelinjer')} ({filteredLines.length})
                </h3>
                {unknownItemCount > 0 && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
                    <AlertTriangle className="h-3 w-3" />
                    {t('warehouse.modules.receipt.lineBrowser.unknownItemCount', { 0: unknownItemCount }, `${unknownItemCount} mangler vare`)}
                  </span>
                )}
              </div>
              {filteredLines.length > 0 && (
                <div className="flex flex-wrap items-center justify-end gap-2">
                  {onDirectPlacement && directPlacementEnabled && (
                    <button
                      onClick={handleDirectPlacement}
                      disabled={selectedLines.length === 0}
                      className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-amber-600 hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <MapPin className="h-3 w-3 mr-1" />
                      {t('warehouse.modules.receipt.lineBrowser.directPlacementShort', {}, 'Direkte ut')} ({selectedLines.length})
                    </button>
                  )}
                  <button
                    onClick={handleReceiveSelected}
                    disabled={selectedLines.length === 0}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <ArrowRight className="h-3 w-3 mr-1" />
                    {t('warehouse.modules.receipt.lineBrowser.receiveSelected', {}, 'Motta valgte')} ({selectedLines.length})
                  </button>
                  <button
                    onClick={handleSelectAllLines}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
                  >
                    <Package className="h-3 w-3 mr-1" />
                    {t('warehouse.modules.receipt.lineBrowser.allLines', {}, 'Alle linjer')}
                  </button>
                </div>
              )}
            </div>

            {filteredLines.length === 0 ? (
              <div className="p-6 text-center text-gray-500 bg-white border border-gray-200 rounded-lg">
                <Package className="h-10 w-10 mx-auto mb-3 text-gray-400" />
                <p className="font-medium">{t('warehouse.modules.receipt.lineBrowser.noLinesFound', {}, 'Ingen ordrelinjer funnet')}</p>
                <p className="text-sm mt-1">{t('warehouse.modules.receipt.lineBrowser.tryAdjustFilterShort', {}, 'Prøv å justere filteret')}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredLines.map((line) => {
                  const bestilt = line.quantity || 0;
                  const mottatt = line.receivedQuantity || 0;
                  const ankommet = line.arrivedQuantity || 0;

                  // Bestem bakgrunnsfarge basert på totalt behandlet (mottatt + ankommet)
                  const totalBehandlet = mottatt + ankommet;
                  const unknown = isUnknownItem(line);
                  let bgColor = 'bg-white'; // Default - ikke mottatt
                  if (unknown) {
                    bgColor = 'bg-red-50'; // Ukjent vare — kan ikke mottas
                  } else if (totalBehandlet >= bestilt && bestilt > 0) {
                    bgColor = 'bg-green-50'; // Fullstendig mottatt
                  } else if (totalBehandlet > 0 && totalBehandlet < bestilt) {
                    bgColor = 'bg-yellow-50'; // Delvis mottatt
                  }

                  return (
                    <div key={`${line.orderNo}-${line.lineNo}`} className={`${bgColor} border border-gray-200 rounded-lg shadow-sm p-3`}>
                      {/* Linje 1: Varenummer, Beskrivelse, Velg-knapp */}
                      <div className="flex items-center justify-between mb-2">
                        <input
                          type="checkbox"
                          checked={selectedKeys.has(lineKey(line))}
                          onChange={() => toggleLine(line)}
                          disabled={unknown}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 flex-shrink-0 mr-2 disabled:opacity-40 disabled:cursor-not-allowed"
                        />
                        <div className="flex-1 min-w-0 mr-2">
                          <div className="flex items-baseline space-x-2 flex-wrap">
                            <span className="font-semibold text-gray-900 text-sm">
                              {line.itemNo}
                            </span>
                            {unknown && (
                              <span className="inline-flex items-center gap-1 rounded bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-700">
                                <AlertTriangle className="h-3 w-3" />
                                {t('warehouse.modules.receipt.lineBrowser.itemNotInMaster', {}, 'Finnes ikke i varemaster')}
                              </span>
                            )}
                            {resolveReceiptExtraInfo(line, extraInfoSlots[0]) && (
                              <span className="text-xs text-gray-700 font-medium">
                                · {resolveReceiptExtraInfo(line, extraInfoSlots[0])}
                              </span>
                            )}
                            <span className="text-xs text-gray-500">
                              - {line.description1 || t('common.noDescription', {}, 'Ingen beskrivelse')}
                            </span>
                          </div>
                          <ReceiptExtraInfo
                            compact
                            className="mt-1"
                            values={resolveReceiptExtraInfoValues(line, [extraInfoSlots[1], extraInfoSlots[2]])}
                          />
                        </div>
                        <button
                          onClick={() => onLineSelect(line)}
                          disabled={unknown}
                          title={unknown ? t('warehouse.modules.receipt.lineBrowser.itemNotInMasterHint', { 0: line.itemNo }, `Vare ${line.itemNo} finnes ikke i varemaster — importer/opprett varen først`) : undefined}
                          className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-blue-600"
                        >
                          <ArrowRight className="h-3 w-3 mr-1" />
                          {t('common.buttons.select', {}, 'Velg')}
                        </button>
                      </div>

                      {/* Linje 2: Bestilt, Mottatt, Ankommet */}
                      <div className="flex items-center space-x-4 text-xs text-gray-700">
                        <span>
                          <span className="text-gray-500">{t('warehouse.modules.receipt.fields.ordered', {}, 'Bestilt')}:</span> <span className="font-medium">{bestilt}</span>
                        </span>
                        <span>
                          <span className="text-gray-500">{t('warehouse.modules.receipt.fields.received', {}, 'Mottatt')}:</span> <span className="font-medium">{mottatt}</span>
                        </span>
                        <span>
                          <span className="text-gray-500">{t('warehouse.modules.receipt.fields.arrived', {}, 'Ankommet')}:</span> <span className="font-medium">{ankommet}</span>
                        </span>
                        <span className="text-gray-500">{line.itemUnitTxt}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};